import { FC, useId } from "react";

type WaveHoleSvgCommonProps = {
  className?: string;
};

type WaveHoleBackgroundSvgProps = WaveHoleSvgCommonProps & {
  fillColor: string;
  gridColor: string;
  shadowColor: string;
};

type WaveHoleTitleSvgProps = WaveHoleSvgCommonProps & {
  mainColor: string;
  shadowColor: string;
};

type WaveHolePeriodSvgProps = WaveHoleSvgCommonProps & {
  fillColor: string;
  iconColor: string;
  outlineColor: string;
};

const WAVE_HOLE_FRAME_PATH =
  "M3.59,19.94v675h1200V19.94H3.59ZM922.03,357.4c0,20.57-11.34,39.27-29.6,48.81-12.04,6.29-21.01,17.21-24.83,30.25-3.82,13.04-2.18,27.07,4.56,38.87,10.23,17.92,9.66,39.81-1.49,57.13-.1.15-.19.3-.29.46-10.21,15.53-27.38,24.79-46.05,24.79h0c-1.64,0-3.3-.07-4.94-.22-1.5-.13-3-.2-4.5-.2-11.99,0-23.63,4.31-32.76,12.23-10.27,8.91-16.47,21.61-17.17,35.18-1.07,20.57-13.35,38.68-32.07,47.25-7.26,3.32-14.94,5.01-22.82,5.01-12.22,0-24.27-4.14-33.93-11.65-8.83-6.87-19.65-10.54-30.7-10.54-2.37,0-4.76.17-7.13.51-13.45,1.94-25.53,9.27-33.46,20.3-10.34,14.38-27,22.96-44.58,22.96-2.63,0-5.28-.19-7.86-.55-20.4-2.9-37.34-16.79-44.2-36.26-4.51-12.81-14.05-23.24-26.41-28.88-6.58-3-13.66-4.51-20.75-4.51-6.23,0-12.46,1.16-18.37,3.5-6.49,2.56-13.33,3.86-20.35,3.86-13.2,0-25.98-4.77-35.98-13.43-.15-.13-.31-.26-.46-.39-15.21-13.48-21.74-34.07-17.06-53.91,3.12-13.23.73-27.16-6.63-38.58-7.36-11.42-19.06-19.36-32.4-21.98-20.25-3.97-36.43-18.72-42.24-38.47-5.81-19.74-.21-40.89,14.62-55.2,9.78-9.44,15.29-22.45,15.28-36.04-.02-13.59-5.56-26.59-15.37-36-14.85-14.26-20.5-35.39-14.74-55.14,5.77-19.77,21.93-34.55,42.17-38.57,13.33-2.65,25.01-10.61,32.34-22.05,7.34-11.44,9.7-25.37,6.55-38.59-4.78-20.03,1.89-40.85,17.4-54.34,10.02-8.71,22.82-13.51,36.05-13.51,6.97,0,13.78,1.29,20.23,3.82,5.88,2.31,12.08,3.46,18.27,3.46,7.13,0,14.24-1.52,20.85-4.55,12.35-5.67,21.86-16.11,26.35-28.94,6.82-19.48,23.72-33.41,44.11-36.35,2.66-.38,5.34-.58,7.98-.58,17.54,0,34.18,8.55,44.53,22.88,7.96,11.02,20.06,18.32,33.51,20.23,2.34.33,4.69.5,7.02.5,11.09,0,21.95-3.69,30.79-10.61,9.68-7.57,21.77-11.73,34.03-11.73,7.84,0,15.48,1.67,22.72,4.97,18.72,8.53,31.05,26.61,32.16,47.19.73,13.57,6.96,26.26,17.25,35.15,9.12,7.88,20.73,12.16,32.68,12.16,1.53,0,3.07-.07,4.6-.21,1.67-.15,3.37-.23,5.05-.23,18.8,0,36.1,9.4,46.29,25.15,11.18,17.3,11.8,39.19,1.61,57.12-6.71,11.81-8.33,25.85-4.48,38.88s12.83,23.93,24.89,30.2c18.27,9.5,29.66,28.16,29.72,48.71v.74Z";
const WAVE_HOLE_FRAME_X = 3.59;
const WAVE_HOLE_FRAME_Y = 19.94;
const WAVE_HOLE_FRAME_WIDTH = 1200;
const WAVE_HOLE_FRAME_HEIGHT = 675;

const WAVE_HOLE_BACKGROUND_SHADOW_PATHS = [
  "M865.79,270.01c-.6,6.3-.02,12.73,1.81,18.92,3.85,13.03,12.83,23.93,24.89,30.2,16.45,8.55,27.32,24.54,29.37,42.64.11-1.44.18-2.9.18-4.36v-.74c-.07-20.55-11.45-39.21-29.72-48.71-12.05-6.27-21.04-17.17-24.89-30.2-.75-2.55-1.29-5.15-1.63-7.75Z",
  "M386,124.16c10.02-8.71,22.82-13.51,36.05-13.51,6.97,0,13.78,1.29,20.23,3.82,5.88,2.31,12.08,3.46,18.27,3.46,7.13,0,14.24-1.52,20.85-4.55,12.35-5.67,21.86-16.11,26.35-28.94,6.82-19.48,23.72-33.41,44.11-36.35,2.66-.38,5.34-.58,7.98-.58,17.54,0,34.18,8.55,44.53,22.88,7.96,11.02,20.06,18.32,33.51,20.23,2.34.33,4.69.5,7.02.5,11.09,0,21.95-3.69,30.79-10.61,9.68-7.57,21.77-11.73,34.03-11.73,7.84,0,15.48,1.67,22.72,4.97,18.72,8.53,31.05,26.61,32.16,47.19.73,13.57,6.96,26.26,17.25,35.15,9.12,7.88,20.73,12.16,32.68,12.16,1.53,0,3.07-.07,4.6-.21,1.67-.15,3.37-.23,5.05-.23,18.8,0,36.1,9.4,46.29,25.15,4.66,7.21,7.48,15.22,8.45,23.4,1.03-11.94-1.86-24.1-8.63-34.57-10.18-15.75-27.49-25.15-46.29-25.15-1.68,0-3.38.08-5.05.23-1.54.14-3.07.21-4.6.21-11.95,0-23.56-4.28-32.68-12.16-10.29-8.88-16.51-21.57-17.25-35.15-1.11-20.58-13.43-38.66-32.16-47.19-7.24-3.3-14.88-4.97-22.72-4.97-12.27,0-24.35,4.17-34.03,11.73-8.84,6.91-19.7,10.61-30.79,10.61-2.34,0-4.68-.16-7.02-.5-13.45-1.91-25.55-9.21-33.51-20.23-10.35-14.32-26.99-22.88-44.53-22.88-2.64,0-5.32.19-7.98.58-20.39,2.94-37.3,16.87-44.11,36.35-4.49,12.83-14,23.27-26.35,28.94-6.61,3.03-13.72,4.55-20.85,4.55-6.19,0-12.39-1.15-18.27-3.46-6.45-2.53-13.26-3.82-20.23-3.82-13.24,0-26.04,4.8-36.05,13.51-13.86,12.05-20.65,29.96-18.54,47.92,1.2-14,7.74-27.21,18.72-36.75Z",
  "M865.99,456.92c-.98,10.2,1.18,20.55,6.34,29.58,3.75,6.57,6.05,13.68,6.9,20.9.95-10.94-1.4-22.12-7.08-32.07-3.27-5.73-5.34-11.99-6.16-18.41Z",
  "M287.54,277.72c5.77-19.77,21.93-34.55,42.17-38.57,13.33-2.65,25.01-10.61,32.34-22.05,6.39-9.96,9-21.8,7.49-33.42-.75,7.85-3.33,15.49-7.67,22.25-7.34,11.44-19.01,19.4-32.34,22.05-20.24,4.02-36.4,18.8-42.17,38.57-2.12,7.26-2.69,14.71-1.83,21.94.31-3.6.97-7.21,2.01-10.77Z",
  "M369.01,559.13c1.4-5.92,1.68-11.98.92-17.9-.22,2.25-.57,4.5-1.1,6.73-1.52,6.42-1.85,12.91-1.1,19.22.23-2.69.65-5.38,1.28-8.05Z",
  "M302.36,404.9c9.78-9.44,15.29-22.45,15.28-36.04,0-2.11-.15-4.2-.42-6.27-1.15,11.78-6.44,22.84-15.04,31.13-12.56,12.12-18.5,29.14-16.49,46.06,1.12-13.03,6.89-25.45,16.67-34.89Z",
];

const WAVE_HOLE_HORIZONTAL_LINES = [46.72, 95.37, 144.02, 192.67, 241.32, 289.97, 338.62, 387.27, 435.92, 484.57, 533.22, 581.87, 630.52, 679.17];
const WAVE_HOLE_VERTICAL_LINES = [55.43, 104.07, 152.7, 201.34, 249.98, 298.62, 347.26, 395.89, 444.53, 493.17, 541.81, 590.45, 639.08, 687.72, 736.36, 785, 833.64, 882.27, 930.91, 979.55, 1028.19, 1076.83, 1125.46, 1174.1];

const WAVE_HOLE_TITLE_TOP_PATHS = [
  "M10.55,0l2.61,14.16L15.9,0h10.31l2.7,13.96L31.61,0h10.55l-7.6,27.16h-10.59l-3-13.8-3.03,13.8H7.4L0,0h10.55Z",
  "M63.14,0v7.72h-10.04v2.05h9.53v7.52h-9.53v2.09h10.43v7.79h-20.55V0h20.16Z",
  "M86.45,0v7.72h-10.04v2.05h9.53v7.52h-9.53v2.09h10.43v7.79h-20.55V0h20.16Z",
  "M99.79,0v7.95l4.37-7.95h11.89l-8.39,11.03,8.94,16.14h-12.12l-3.72-8.97-.97,1.21v7.75h-10.2V0h10.2Z",
  "M127.66,0v18.9h9.17v8.27h-19.41V0h10.23Z",
  "M136.25,0h11.26l3.27,8.94,3.31-8.94h11.18l-9.41,17.32v9.84h-10.24v-9.84L136.25,0Z",
];

const WAVE_HOLE_TITLE_BOTTOM_PATHS = [
  "M11.89,60.83c.94,0,1.61-.24,1.61-.63,0-.47-.59-.62-2.48-.95l-.36-.06c-4.29-.73-9.68-3.26-9.68-9.03,0-6.59,5.99-9.33,11.73-9.33,3.86,0,7.6,1.18,10.2,3.23l-3.74,6.73c-2.91-1.93-5-2.44-6.85-2.44-.94,0-1.73.2-1.73.67,0,.5,1.08.63,2.31.86l.42.08c4.3.82,9.63,3.2,9.63,8.54,0,7.4-6.17,10.2-12.09,10.2-2.76,0-6.42-.28-10.87-3.39l3.98-7.32c2.64,1.93,5.9,2.83,7.91,2.83Z",
  "M38.46,68.7c-8.11,0-14.72-4.8-14.72-14.01s6.57-14.01,14.72-14.01c5.94,0,8.9,2.32,8.9,2.32l-2.56,8.58s-2.4-1.85-5.63-1.85c-2.72,0-5.04,1.73-5.04,4.84s2.24,4.88,5.04,4.88c2.6,0,4.76-1.18,6.06-2.17l2.79,8.15c-.79.75-3.5,3.27-9.57,3.27Z",
  "M49.48,41.06h10.2v9.53h5.59v-9.53h10.2v27.16h-10.2v-9.37h-5.59v9.37h-10.2v-27.16Z",
  "M99.01,41.06v7.72h-10.04v2.05h9.53v7.52h-9.53v2.09h10.43v7.79h-20.55v-27.16h20.16Z",
  "M123.73,43.86c2.68,1.97,4.92,5.16,4.92,10.79s-2.09,8.74-4.76,10.75c-2.72,2.01-6.26,2.83-10.98,2.83h-10.75v-27.16h10.75c4.72,0,8.11.83,10.83,2.79ZM117.11,58.62c.6-.62,1.14-1.5,1.14-3.98s-.53-3.44-1.18-4.09c-.65-.65-1.69-1.18-3.35-1.18h-1.34v10.35h1.34c1.65,0,2.77-.46,3.39-1.1Z",
  "M141.52,59.13c.47.55,1.18.79,2.01.79s1.5-.24,1.93-.67c.51-.51.75-1.3.75-2.24v-15.94h10.24v15.47c0,4.57-1.14,7.09-3.11,9.01-2.01,1.97-5.28,3.15-9.8,3.15s-7.72-1.14-9.76-3.11c-2.01-1.93-3.15-4.49-3.15-9.05v-15.47h10.2v15.94c0,.95.28,1.65.71,2.13Z",
  "M169.9,41.06v18.9h9.17v8.27h-19.41v-27.16h10.23Z",
  "M201.32,41.06v7.72h-10.04v2.05h9.53v7.52h-9.53v2.09h10.43v7.79h-20.55v-27.16h20.16Z",
];

const WAVE_HOLE_PERIOD_PRIMARY_PATH =
  "M83.52,134.86c-5.52,0-10.76-2.37-14.4-6.5-1.57-1.79-3.84-2.81-6.22-2.81-.5,0-1,.04-1.49.13-1.36.25-2.65.84-3.72,1.71-2.49,2.02-5.48,3.39-8.64,3.96-1.14.21-2.3.31-3.44.31-3.05,0-5.99-.72-8.74-2.14-6.75-3.48-10.8-10.64-10.32-18.23.28-4.45-2.97-8.29-7.4-8.76-7.55-.79-13.93-5.98-16.24-13.22-2.31-7.24-.11-15.16,5.59-20.18,3.34-2.94,3.77-7.96.96-11.42-4.78-5.9-5.62-14.08-2.14-20.83,2.74-5.32,7.7-8.98,13.59-10.05,1.13-.21,2.28-.31,3.42-.31.4,0,.8.01,1.2.04.18.01.35.02.53.02.5,0,1-.04,1.48-.13,3.59-.65,6.36-3.65,6.74-7.28.79-7.56,5.98-13.94,13.21-16.25.81-.26,1.62-.46,2.43-.61,1.11-.2,2.24-.3,3.37-.3,5.51,0,10.75,2.37,14.38,6.49,1.57,1.78,3.84,2.81,6.22,2.81.5,0,1-.04,1.49-.13,1.36-.25,2.64-.84,3.72-1.71,2.5-2.02,5.49-3.4,8.67-3.98,1.14-.21,2.3-.31,3.44-.31,3.05,0,5.98.72,8.73,2.14,6.76,3.48,10.81,10.63,10.33,18.21-.28,4.45,2.97,8.29,7.4,8.76,7.49.78,13.86,5.9,16.21,13.05l.08.26c2.31,7.25.1,15.17-5.61,20.19-3.35,2.94-3.77,7.96-.97,11.42,4.78,5.91,5.61,14.08,2.12,20.83-2.75,5.31-7.7,8.97-13.58,10.04-1.13.21-2.29.31-3.43.31-.41,0-.81-.01-1.21-.04-.18-.01-.36-.02-.54-.02-.5,0-1,.04-1.48.13-3.59.65-6.36,3.64-6.74,7.27-.8,7.56-5.99,13.93-13.23,16.24-.8.25-1.61.45-2.41.6-1.11.2-2.24.3-3.37.3Z";

const WAVE_HOLE_PERIOD_OUTLINE_PATH =
  "M53.31,4c4.86,0,9.61,2.09,12.88,5.81,2.03,2.3,4.86,3.49,7.72,3.49.62,0,1.23-.06,1.84-.17,1.64-.3,3.24-1,4.62-2.13,2.24-1.81,4.92-3.04,7.77-3.56,1.03-.19,2.06-.28,3.08-.28,2.7,0,5.35.65,7.81,1.91,6.06,3.12,9.68,9.52,9.25,16.31-.35,5.5,3.71,10.3,9.19,10.87,6.72.7,12.41,5.28,14.52,11.69,0,.03.06.2.08.26,2.06,6.48.09,13.58-5.02,18.07-4.14,3.64-4.67,9.9-1.2,14.19,4.28,5.29,5.03,12.61,1.9,18.66-2.46,4.75-6.89,8.03-12.16,8.99t0,0c-1.02.18-2.04.28-3.07.28-.36,0-.72-.01-1.09-.03-.22-.01-.45-.02-.67-.02-.63,0-1.24.06-1.84.17-4.39.8-7.89,4.4-8.37,9.03-.71,6.77-5.37,12.47-11.85,14.54-.7.22-1.44.41-2.16.54-1,.18-2.01.27-3.01.27-4.87,0-9.62-2.1-12.9-5.82-2.03-2.3-4.86-3.49-7.72-3.49-.62,0-1.23.06-1.84.17-1.65.3-3.24,1-4.63,2.13-2.23,1.81-4.9,3.03-7.74,3.55-1.03.19-2.06.28-3.08.28-2.7,0-5.36-.65-7.82-1.92-6.05-3.12-9.67-9.53-9.24-16.33.35-5.5-3.7-10.3-9.18-10.87-6.77-.71-12.47-5.36-14.55-11.84-2.07-6.48-.1-13.58,5.01-18.08,4.14-3.64,4.66-9.9,1.2-14.18-4.28-5.29-5.04-12.61-1.91-18.66,2.46-4.76,6.9-8.04,12.17-9,1.01-.18,2.04-.28,3.07-.28.36,0,.72.01,1.07.03.22.01.44.02.66.02.63,0,1.24-.06,1.84-.17,4.39-.8,7.89-4.41,8.37-9.04.71-6.77,5.35-12.48,11.83-14.55.72-.23,1.45-.41,2.18-.54,1-.18,2.01-.27,3.01-.27M53.31,0c-1.25,0-2.5.11-3.73.34-.9.16-1.8.39-2.68.67-7.99,2.55-13.72,9.6-14.6,17.94-.29,2.76-2.39,5.03-5.11,5.52-.37.07-.74.1-1.12.1-.14,0-.27,0-.41-.01-.44-.03-.89-.04-1.33-.04-1.26,0-2.54.11-3.78.34-6.51,1.18-11.98,5.23-15.01,11.1-3.85,7.46-2.92,16.49,2.36,23.01,2.13,2.63,1.81,6.43-.73,8.66-6.3,5.55-8.72,14.3-6.17,22.29,2.55,8,9.6,13.73,17.94,14.6,3.36.35,5.82,3.27,5.61,6.64-.53,8.38,3.94,16.29,11.4,20.13,3.03,1.57,6.28,2.37,9.66,2.37,1.26,0,2.54-.12,3.8-.34,3.5-.64,6.8-2.15,9.55-4.38.83-.67,1.78-1.11,2.82-1.3.37-.07.75-.1,1.13-.1,1.81,0,3.53.78,4.72,2.13,4.01,4.56,9.81,7.18,15.9,7.18,1.25,0,2.5-.11,3.73-.34.88-.16,1.78-.38,2.65-.66,8.01-2.55,13.75-9.59,14.63-17.93.29-2.75,2.39-5.02,5.11-5.52.37-.07.75-.1,1.12-.1.14,0,.27,0,.41.01.44.03.9.04,1.34.04,1.26,0,2.54-.11,3.79-.34,6.5-1.18,11.97-5.22,15-11.09,3.86-7.45,2.94-16.49-2.34-23.01-2.13-2.63-1.8-6.43.74-8.66,6.31-5.54,8.74-14.29,6.2-22.29l-.06-.2v-.05s-.03-.05-.03-.05c-2.6-7.89-9.63-13.55-17.9-14.41-3.36-.35-5.83-3.27-5.61-6.64.53-8.37-3.95-16.27-11.41-20.11-3.03-1.56-6.27-2.36-9.64-2.36-1.26,0-2.54.12-3.8.34-3.5.64-6.81,2.16-9.57,4.39-.83.67-1.78,1.11-2.82,1.3-.37.07-.75.1-1.13.1-1.81,0-3.52-.78-4.72-2.13-4.01-4.55-9.8-7.17-15.88-7.17h0Z";

export const WaveHoleBackgroundSvg: FC<WaveHoleBackgroundSvgProps> = ({
  className,
  fillColor,
  gridColor,
  shadowColor,
}) => {
  const clipPathId = useId().replace(/:/g, "_");

  return (
    <svg
      aria-hidden="true"
      className={className}
      preserveAspectRatio="none"
      viewBox={`${WAVE_HOLE_FRAME_X} ${WAVE_HOLE_FRAME_Y} ${WAVE_HOLE_FRAME_WIDTH} ${WAVE_HOLE_FRAME_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={clipPathId}>
          <path d={WAVE_HOLE_FRAME_PATH} />
        </clipPath>
      </defs>
      <g>
        <g opacity={0.5}>
          {WAVE_HOLE_BACKGROUND_SHADOW_PATHS.map((path) => (
            <path key={path.slice(0, 32)} d={path} fill={shadowColor} />
          ))}
        </g>
        <path d={WAVE_HOLE_FRAME_PATH} fill={fillColor} />
        <g clipPath={`url(#${clipPathId})`}>
          {WAVE_HOLE_HORIZONTAL_LINES.map((lineY) => (
            <line
              key={`h-${lineY}`}
              stroke={gridColor}
              strokeMiterlimit={10}
              strokeWidth={2}
              x1={0}
              x2={1229.53}
              y1={lineY}
              y2={lineY}
            />
          ))}
          {WAVE_HOLE_VERTICAL_LINES.map((lineX) => (
            <line
              key={`v-${lineX}`}
              stroke={gridColor}
              strokeMiterlimit={10}
              strokeWidth={2}
              x1={lineX}
              x2={lineX}
              y1={0}
              y2={706.66}
            />
          ))}
        </g>
      </g>
    </svg>
  );
};

export const WaveHoleTitleSvg: FC<WaveHoleTitleSvgProps> = ({ className, mainColor, shadowColor }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 201.71 68.7"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <g>
        {WAVE_HOLE_TITLE_BOTTOM_PATHS.map((path) => (
          <path key={`shadow-bottom-${path.slice(0, 24)}`} d={path} fill={shadowColor} />
        ))}
        {WAVE_HOLE_TITLE_BOTTOM_PATHS.map((path) => (
          <path key={`main-bottom-${path.slice(0, 24)}`} d={path} fill={mainColor} />
        ))}
      </g>
      <g>
        {WAVE_HOLE_TITLE_TOP_PATHS.map((path) => (
          <path key={`shadow-top-${path.slice(0, 24)}`} d={path} fill={shadowColor} />
        ))}
        {WAVE_HOLE_TITLE_TOP_PATHS.map((path) => (
          <path key={`main-top-${path.slice(0, 24)}`} d={path} fill={mainColor} />
        ))}
      </g>
    </g>
  </svg>
);

export const WaveHolePeriodSvg: FC<WaveHolePeriodSvgProps> = ({
  className,
  fillColor,
  iconColor,
  outlineColor,
}) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 136.88 136.86"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <path d={WAVE_HOLE_PERIOD_PRIMARY_PATH} fill={fillColor} />
      <path d={WAVE_HOLE_PERIOD_OUTLINE_PATH} fill={outlineColor} />
      <polygon fill={iconColor} points="74.9 67.87 68.1 74.2 61.98 67.87 74.9 67.87" />
    </g>
  </svg>
);
